import React from 'react';

// Renders the client/device context captured per request by the shipping backend
// (backend/phone/utils/clientContext.js), stored on customer_activity_log as
// ip_address + metadata.device.
//
// Only explicit audit rows carry this. Derived feed rows (orders, rate requests,
// last-login) are reconstructed from other tables and have no request attached, so
// this renders nothing for them — as do rows written before 2026-09-11, when capture
// started. Returning null keeps those timelines visually unchanged.
//
// Reading the IP: before 2026-09-11 the backend stored req.ip, which behind
// Cloudflare + nginx was the Cloudflare EDGE address (104.16/13, 162.158/15,
// 172.64/13), not the customer. Those historical rows are not customer IPs.

const FLAG_HELP = {
  ua_casing_anomaly:
    'User-agent casing no real browser emits (e.g. "Mac Os X") — a hand-built header string',
  ua_automation_tool: 'User-agent names a headless/automation tool',
  ua_http_client: 'Request came from a raw HTTP client, not a browser',
  ua_missing_client_hints: 'Claims Chrome but sent no Sec-CH-UA client hints',
  ua_platform_mismatch: 'User-agent OS disagrees with the Sec-CH-UA platform hint',
  missing_user_agent: 'No user-agent header sent',
};

// Provenance matters when reading an IP: cf-connecting-ip is overwritten by Cloudflare
// on every request and cannot be forged by the client, whereas x-forwarded-for is
// client-appendable and should be trusted less.
function ipTitle(device) {
  const src = device.ip_source || 'unknown';
  const trusted = src === 'cf-connecting-ip' || src === 'true-client-ip';
  return `Source: ${src}${
    trusted
      ? ' (trusted — set by Cloudflare, not client-forgeable)'
      : ' (weaker — the client can influence this header)'
  }`;
}

export default function ClientContextLine({ row, className = '' }) {
  const device = (row && row.metadata && row.metadata.device) || {};
  const ip = row && row.ip_address;
  const flags = Array.isArray(device.ua_flags) ? device.ua_flags : [];

  if (!ip && !device.country && !device.os && flags.length === 0) return null;

  const bits = [];
  if (device.country) bits.push(device.country);
  if (device.os) bits.push(device.browser ? `${device.os} · ${device.browser}` : device.os);
  if (device.ua_mobile === '?1') bits.push('mobile');
  if (device.cf_colo) bits.push(`via ${device.cf_colo}`);
  if (device.via) bits.push('proxy hdr');

  return (
    <div
      className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-400 ${className}`}
    >
      {ip && (
        <span className="font-mono text-gray-500 break-all" title={ipTitle(device)}>
          {ip}
        </span>
      )}
      {bits.length > 0 && <span className="break-words">{bits.join(' · ')}</span>}
      {device.accept_language && (
        <span className="hidden sm:inline" title="Browser Accept-Language">
          {String(device.accept_language).split(',')[0]}
        </span>
      )}
      {flags.map((f) => (
        <span
          key={f}
          title={FLAG_HELP[f] || 'Automated-client signal — a review hint, not proof'}
          className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200"
        >
          ⚠ {f.replace(/^ua_/, '').replace(/_/g, ' ')}
        </span>
      ))}
    </div>
  );
}
